/**
 * The directory board, built the way the 90s mall maps were printed: one grey building
 * silhouette with a heavy outer wall, WIDE concourses running through it, big anchor rooms at
 * the ends of every wing, inline storefronts lining both sides of the halls (small units, some
 * merged three or four wide for a bigger shop), carts standing out in the hallway, and the
 * plaza off on its own as a food-court wing with 13thav's shared rooms around it.
 *
 * Colour comes from the WING, not the shop — a whole wing reads as one mass.
 * The envelope is fully tiled: whatever isn't a room is hallway, so there are no gaps
 * and nothing overlaps.
 */
const W = 1600;
const H = 1040;

/**
 * Wing colours, straight off a 90s outlet-mall board: one saturated colour per wing, grey
 * concourse, heavy grey outer wall.
 */
// Monochrome: the directory is generic on purpose — the color belongs to each shop's own page.
export const ZONES = {
    west: "#18181B",
    east: "#27272A",
    south: "#3F3F46",
    court: "#F4F4F5",
    room: "#E4E4E7",
    cart: "#A1A1AA",
};
export const OUTLINE = "#71717A";
export const INK = "#18181B";
export const FAINT = "#71717A";
export const HALL = "#FFFFFF";
export const WALL = "#D4D4D8";

/** 13thav's own rooms. They work floor-wide but each one has a door on the board. */
/** The shared pages on the map are the shopper menu — same six words as the header. */
export const MALL_ROOMS = {
    plaza: { name: "Classifieds", sub: "community · wholesale", to: "/supplies" },
    office: { name: "Shirts", sub: "tees · hoodies · DTF", to: "/shirts" },
    art: { name: "Wholesale", sub: "coupons · bulk", to: "/wholesale" },
    classroom: { name: "Classes", sub: "learn to print", to: "/classes" },
    web: { name: "Signs", sub: "banners · vinyl", to: "/signs" },
};

/** The outer wall. Follows the anchors as they step out past the concourse. */
export const SHELL = [
    [560, 120], [1040, 120], [1040, 300], [1350, 300], [1350, 260], [1540, 260], [1590, 310],
    [1590, 700], [1350, 700], [1350, 670], [1080, 670], [1080, 800], [1130, 800], [1130, 980],
    [470, 980], [470, 800], [520, 800], [520, 670], [250, 670], [250, 700], [10, 700],
    [10, 310], [60, 260], [250, 260], [250, 300], [560, 300],
].map((p) => p.join(",")).join(" ");

/** Four anchor rooms — the big boxes at the end of each wing, corners cut like the old plans. */
const ANCHORS = [
    {
        id: "a-west", zone: "west", x: 10, y: 260, w: 240, h: 440, numAt: [24, 300],
        points: "60,260 250,260 250,700 10,700 10,310",
    },
    {
        id: "a-east", zone: "east", x: 1350, y: 260, w: 240, h: 440, numAt: [1362, 284],
        points: "1350,260 1540,260 1590,310 1590,700 1350,700",
    },
    { id: "a-southwest", zone: "south", x: 470, y: 800, w: 330, h: 180, numAt: [484, 826] },
    { id: "a-southeast", zone: "south", x: 800, y: 800, w: 330, h: 180, numAt: [814, 826] },
];

/**
 * Inline units. The merged ones (three or four small shops knocked together) come first so a
 * real storefront always lands in a proper room; the single small units stay on the board as
 * numbered vacancies.
 */
const INLINE = [
    { id: "i-n1", zone: "west", x: 250, y: 300, w: 170, h: 130 },
    { id: "i-n4", zone: "west", x: 590, y: 300, w: 155, h: 130 },
    { id: "i-n5", zone: "east", x: 855, y: 300, w: 155, h: 130 },
    { id: "i-n8", zone: "east", x: 1180, y: 300, w: 170, h: 130 },
    { id: "i-s1", zone: "west", x: 250, y: 540, w: 170, h: 130 },
    { id: "i-s4", zone: "west", x: 590, y: 540, w: 155, h: 130 },
    { id: "i-s5", zone: "east", x: 855, y: 540, w: 155, h: 130 },
    { id: "i-s8", zone: "east", x: 1180, y: 540, w: 170, h: 130 },
    { id: "i-w1", zone: "south", x: 520, y: 670, w: 225, h: 130 },
    { id: "i-w2", zone: "south", x: 855, y: 670, w: 225, h: 130 },
    { id: "i-n2", zone: "west", x: 420, y: 300, w: 85, h: 130 },
    { id: "i-n3", zone: "west", x: 505, y: 300, w: 85, h: 130 },
    { id: "i-n6", zone: "east", x: 1010, y: 300, w: 85, h: 130 },
    { id: "i-n7", zone: "east", x: 1095, y: 300, w: 85, h: 130 },
    { id: "i-s2", zone: "west", x: 420, y: 540, w: 85, h: 130 },
    { id: "i-s3", zone: "west", x: 505, y: 540, w: 85, h: 130 },
    { id: "i-s6", zone: "east", x: 1010, y: 540, w: 85, h: 130 },
    { id: "i-s7", zone: "east", x: 1095, y: 540, w: 85, h: 130 },
];

/** Carts stand out in the concourse, spread along the main hall. */
const CART = 60;
const CART_SPOTS = [[300, 455], [450, 455], [620, 455], [990, 455], [1140, 455], [1290, 455]];
const CARTS = CART_SPOTS.map(([x, y], i) => ({ id: `c-${i}`, zone: "cart", x, y, w: CART, h: CART }));

/** The plaza wing: the court in the middle, 13thav's shared rooms either side of it. */
const COURT = { x: 700, y: 120, w: 200, h: 180 };
const ROOMS = [
    { id: "room-plaza", room: "plaza", zone: "court", ...COURT },
    { id: "room-office", room: "office", zone: "room", x: 560, y: 120, w: 140, h: 90 },
    { id: "room-art", room: "art", zone: "room", x: 560, y: 210, w: 140, h: 90 },
    { id: "room-classroom", room: "classroom", zone: "room", x: 900, y: 120, w: 140, h: 90 },
    { id: "room-web", room: "web", zone: "room", x: 900, y: 210, w: 140, h: 90 },
];

/** Mall doors, lettered the way the boards lettered them. */
export const ENTRANCES = [
    { key: "A", x: 780, y: 108, w: 40, h: 12, labelAt: [800, 100], anchor: "middle" },
    { key: "B", x: 780, y: 980, w: 40, h: 12, labelAt: [800, 1008], anchor: "middle" },
    { key: "C", x: 0, y: 460, w: 12, h: 40, labelAt: [20, 452], anchor: "start" },
    { key: "D", x: 1588, y: 460, w: 12, h: 40, labelAt: [1580, 452], anchor: "end" },
];
/**
 * The pad site: same property, its own building, its own parking. Rent this one and you get
 * everything on this website — products, the live designers, billboards — as your own private
 * site for your business, with the mall hidden.
 */
export const PAD = {
    id: "pad-site", kind: "pad", unit: "P1", x: 40, y: 760, w: 350, h: 190,
    points: "80,760 390,760 390,950 40,950 40,800",
};
/**
 * The other pad, already taken: 13thav's own brick & mortar shop. Same deal as P1 — its own
 * building on the same property, its own white-label site, the mall out of the way.
 */
export const OWN_PAD = {
    id: "pad-own", kind: "pad", unit: "P2", x: 1200, y: 760, w: 350, h: 190,
    points: "1200,760 1510,760 1550,800 1550,950 1200,950",
};
export const HERE = { x: 800, y: 470 };
export const VIEW = { w: W, h: H };

/** White type on a dark mass, near-black on a bright one. */
export const readableOn = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return INK;
    const n = parseInt(h, 16);
    const lum = (((n >> 16) & 255) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114) / 255;
    return lum > 0.5 ? INK : "#ffffff";
};

/** Every active shop is on the board. */
export const isOpen = () => true;

/**
 * When the main building is full, the directory grows: a new wing is built under the last
 * one, with two Pro Shop rooms at the ends, sixteen storefront units along both walls and six
 * carts in the hall. Wings are added until every shop has a unit.
 */
const WING_TOP = 1070;
const WING_H = 380;
const WING_GAP = 40;
const wingSlots = (k) => {
    const top = WING_TOP + k * (WING_H + WING_GAP);
    const tag = `w${k + 1}`;
    const anchors = [
        { id: `${tag}-a-west`, zone: "west", x: 10, y: top, w: 240, h: WING_H, numAt: [24, top + 26], tier: "department" },
        { id: `${tag}-a-east`, zone: "east", x: 1350, y: top, w: 240, h: WING_H, numAt: [1362, top + 26], tier: "department" },
    ];
    const unitW = 1100 / 8;
    const inline = [];
    for (let i = 0; i < 8; i++) {
        const x = 250 + i * unitW;
        const zone = i < 4 ? "west" : "east";
        inline.push({ id: `${tag}-n${i}`, zone, x, y: top, w: unitW, h: 130, tier: "storefront" });
        inline.push({ id: `${tag}-s${i}`, zone, x, y: top + WING_H - 130, w: unitW, h: 130, tier: "storefront" });
    }
    const carts = [300, 450, 620, 990, 1140, 1290].map((x, i) => ({
        id: `${tag}-c${i}`, zone: "cart", x, y: top + WING_H / 2 - CART / 2, w: CART, h: CART, tier: "kiosk",
    }));
    const shell = [[10, top], [1590, top], [1590, top + WING_H], [10, top + WING_H]].map((p) => p.join(",")).join(" ");
    return { slots: [...anchors, ...inline, ...carts], shell: { points: shell, label: `Wing ${k + 2}`, at: [800, top - 12] }, bottom: top + WING_H };
};

/** Extra private-site pads sit in a row under the last wing, four to a row. */
const PAD_W = 350;
const PAD_H = 190;
const padAt = (i, top) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const x = 40 + col * 390;
    const y = top + row * (PAD_H + 90);
    return { id: `pad-${i + 3}`, kind: "pad", unit: `P${i + 3}`, x, y, w: PAD_W, h: PAD_H,
        points: `${x},${y} ${x + PAD_W - 40},${y} ${x + PAD_W},${y + 40} ${x + PAD_W},${y + PAD_H} ${x},${y + PAD_H}` };
};

export const wrapName = (name = "", per = 15, lines = 2) => {
    const out = [];
    let line = "";
    name.trim().split(/\s+/).forEach((word) => {
        if (!line) line = word;
        else if (`${line} ${word}`.length <= per) line = `${line} ${word}`;
        else { out.push(line); line = word; }
    });
    if (line) out.push(line);
    if (out.length <= lines) return out;
    return [...out.slice(0, lines - 1), `${out[lines - 1].slice(0, per - 1)}…`];
};

export const buildPlan = (floor) => {
    const pools = {
        department: floor?.anchors || [],
        storefront: floor?.storefronts || [],
        kiosk: floor?.kiosks || [],
    };

    const base = [
        ...ANCHORS.map((b) => ({ ...b, tier: "department" })),
        ...INLINE.map((b) => ({ ...b, tier: "storefront" })),
        ...CARTS.map((b) => ({ ...b, tier: "kiosk" })),
        ...ROOMS.map((r) => ({ ...r, kind: "room", store: null })),
    ];

    // the building grows: add a wing whenever any plan has more shops than open units
    const wings = [];
    let bottom = H;
    const capacity = (tier, list) => list.filter((s) => s.tier === tier).length;
    for (let k = 0; k < 12; k++) {
        const all = [...base, ...wings.flatMap((w) => w.slots)];
        const full = Object.entries(pools).some(([tier, shops]) => shops.length > capacity(tier, all));
        if (!full) break;
        const wing = wingSlots(k);
        wings.push(wing);
        bottom = wing.bottom;
    }

    const slots = [...base, ...wings.flatMap((w) => w.slots)]
        .map((b) => ({ ...b, kind: b.kind || "vacant", store: b.store || null, color: ZONES[b.zone] }));

    // unit numbers first, read the way the old boards printed them, so a number never moves
    [...slots].sort((a, b) => (a.y - b.y) || (a.x - b.x)).forEach((s, i) => { s.unit = i + 1; });

    // a shop that asked the leasing office for a unit gets that unit; everybody else fills in order
    Object.entries(pools).forEach(([tier, shops]) => {
        const open = slots.filter((s) => s.tier === tier && s.kind === "vacant");
        const waiting = [];
        shops.forEach((shop) => {
            const want = shop.unit ? open.find((s) => String(s.unit) === String(shop.unit) && !s.store) : null;
            if (want) { want.store = shop; want.kind = "store"; } else waiting.push(shop);
        });
        waiting.forEach((shop) => {
            const next = open.find((s) => !s.store);
            if (next) { next.store = shop; next.kind = "store"; }
        });
    });

    // private sites: P1 stays open for the next tenant, P2 is the house shop, the rest get pads in a row
    const own = floor?.own_sites || [];
    const pads = [PAD, ...(own[0] ? [{ ...OWN_PAD, store: own[0] }] : [])];
    const extra = own.slice(1);
    const padTop = (wings.length ? bottom : H) + 50;
    extra.forEach((s, i) => pads.push({ ...padAt(i, padTop), store: s }));
    const padBottom = extra.length ? padTop + Math.ceil(extra.length / 4) * (PAD_H + 90) : bottom;
    const view = { w: W, h: Math.max(H, padBottom + 20) };

    return {
        view, slots, shell: SHELL, wings: wings.map((w) => w.shell), entrances: ENTRANCES, here: HERE,
        court: COURT, pad: PAD, pads,
    };
};

/** The board slot a given shop is sitting in, so its billboard can reuse the same block. */
export const slotOf = (plan, slug) => plan.slots.find((s) => s.store?.slug === slug) || null;
